import { commandPayload, commands, isLiveRuntime, uploadImage } from '../../lib/runtime'
import type { ImageAdvisory } from '../../lib/imageProcessing'
import { confirmedUpsert, enumShort } from './chefShared'

type Row = Record<string, unknown>

/**
 * Save a chef's photo everywhere customers see it.
 *
 * The base profile carries the account avatar, but a kitchen's public face
 * lives on the vendor profile, so a photo that only reached chow_profiles never
 * showed on kitchen cards. This writes both, keeping the vendor's existing
 * type, name, cuisine and area intact. The upload's soft photo advice comes
 * back with the URL so the caller can show it.
 */
export const saveChefPhoto = async ({
  file,
  profileId,
  fallbackOwner,
  vendor,
  displayName,
  updateProfile,
}: {
  file: File
  profileId: string
  fallbackOwner: string
  vendor: Row | undefined
  displayName: string
  updateProfile: (input: { avatarUrl: string }) => Promise<unknown>
}): Promise<{ url: string; advisories: ImageAdvisory[] }> => {
  const uploaded = await uploadImage(file, 'profile', profileId || fallbackOwner || 'me')
  await updateProfile({ avatarUrl: uploaded.url })
  if (profileId && isLiveRuntime()) {
    const saved = await commands.profile.setVendorProfile(profileId, {
      vendor_type: enumShort(vendor?.vendor_type, 'home_chef'),
      display_name: String(vendor?.display_name ?? displayName ?? 'My kitchen'),
      cuisine: String(vendor?.cuisine ?? ''),
      location_label: String(vendor?.location_label ?? ''),
      avatar_image_url: uploaded.url,
    })
    confirmedUpsert('profile.vendor_profiles', { ...vendor, ...commandPayload(saved), profile_id: profileId, avatar_image_url: uploaded.url })
  }
  return { url: uploaded.url, advisories: uploaded.advisories ?? [] }
}
